import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard } from '../components/StaticPageShell';

const FAQ = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const items = [
        // Chat messaging deferred — do not promise seller messaging until chat ships
        {
            q: 'page.faq.q1',
            qFallback: 'How do I buy an item on Petito?',
            a: 'page.faq.a1',
            aFallback:
                'Browse listings and pay securely at checkout with Bancontact, credit card, or PayPal. After delivery, you can report a problem via your order or the helpdesk if something is wrong.',
        },
        {
            q: 'page.faq.q2',
            qFallback: 'Is shipping available across Belgium?',
            a: 'page.faq.a2',
            aFallback:
                'Yes. Most sellers ship via bpost, DPD, or Mondial Relay within Belgium. Shipping costs are calculated at secure checkout (not as a fixed price on each listing).',
        },
        {
            q: 'page.faq.q3',
            qFallback: 'Can I return an item?',
            a: 'page.faq.a3',
            aFallback:
                'Private sales between individuals are generally final unless the item was misdescribed. See our Shipping & Returns page for full details and your EU consumer rights where applicable.',
        },
        {
            q: 'page.faq.q4',
            qFallback: 'How does Petito keep baby gear safe?',
            a: 'page.faq.a4',
            aFallback:
                'We prohibit recalled products, unsafe car seats, and counterfeit goods. Sellers must describe condition honestly and follow our Community Rules.',
        },
        {
            q: 'page.faq.q5',
            qFallback: 'How do I sell my baby items?',
            a: 'page.faq.a5',
            aFallback:
                'Create a free account and list your item with clear photos and an honest description. Buyers pay a small buyer protection fee at checkout; you keep the list price of your item.',
        },
        {
            q: 'page.faq.q6',
            qFallback: 'Where is Petito based?',
            a: 'page.faq.a6',
            aFallback:
                'Petito is a Belgian marketplace serving parents across Belgium and neighbouring EU countries, with support in Dutch, French, and English.',
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.faq.eyebrow', 'Help')}
            title={tr('page.faq.title', 'Frequently Asked Questions')}
            subtitle={tr(
                'page.faq.intro',
                'Quick answers for Belgian parents buying and selling pre-loved baby gear on Petito.'
            )}
            maxWidth="3xl"
        >
            <div className="space-y-3">
                {items.map((item, index) => (
                    <details
                        key={item.q}
                        className="group rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_35%)] shadow-sm open:shadow-md"
                    >
                        <summary className="flex min-h-11 cursor-pointer list-none items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_92%)] sm:px-5 sm:py-4 sm:text-base [&::-webkit-details-marker]:hidden">
                            <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--accent-color),transparent_75%)] text-xs font-bold text-[var(--text-color)] sm:h-9 sm:w-9 sm:text-sm"
                                aria-hidden
                            >
                                {index + 1}
                            </span>
                            <span className="flex-1 pr-2">{tr(item.q, item.qFallback)}</span>
                            <span
                                className="shrink-0 text-lg text-[var(--accent-color)] transition group-open:rotate-45"
                                aria-hidden
                            >
                                +
                            </span>
                        </summary>
                        <div className="border-t border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] px-4 pb-4 pt-3 text-sm leading-relaxed text-[var(--text-color)]/90 sm:px-5 sm:pb-5 sm:pl-[4.25rem] sm:text-base">
                            <p>{tr(item.a, item.aFallback)}</p>
                            {item.q === 'page.faq.q3' && (
                                <p className="mt-2">
                                    <Link
                                        to="/shipping"
                                        className="font-medium text-[var(--accent-color)] underline underline-offset-2"
                                    >
                                        {tr('page.shipping.title', 'Shipping & Returns')}
                                    </Link>
                                </p>
                            )}
                            {item.q === 'page.faq.q4' && (
                                <p className="mt-2">
                                    <Link
                                        to="/rules"
                                        className="font-medium text-[var(--accent-color)] underline underline-offset-2"
                                    >
                                        {tr('page.rules.title', 'Community Rules – Petito')}
                                    </Link>
                                </p>
                            )}
                        </div>
                    </details>
                ))}
            </div>

            <StaticCtaCard>
                <p className="mb-3">
                    {tr('page.faq.contact', 'Still have questions?')}
                </p>
                <Link
                    to="/helpdesk"
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                    {tr('page.faq.contact.link', 'Contact our helpdesk')}
                </Link>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default FAQ;
